import React, { useState, memo, useCallback } from "react";
import { ArrowRight, Check, Clipboard, Upload, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { ApplicationFormDialog } from "./ApplicationFormDialog";

interface Step {
  id: number;
  title: string;
  icon: React.ReactNode;
  description: string;
  details: string[];
}

interface DetailsTriggerProps {
  step: Step;
}

const HowItWorksSection = memo(() => {
  const isMobile = useIsMobile();
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  
  const handleApplicationFormOpen = useCallback(() => {
    setShowApplicationForm(true);
  }, []);
  
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  
  const steps = [{
    id: 1,
    title: "Inscription",
    icon: <Check className="h-6 w-6 text-primary" />,
    description: "Postule en ligne et rejoins l'équipe de créateurs partenaires du film.",
    details: ["Remplis un formulaire ultra rapide.", "Passe un appel de sélection avec nous.", "Intègre le groupe privé avec les autres créateurs retenus."]
  }, {
    id: 2,
    title: "Création",
    icon: <Clipboard className="h-6 w-6 text-primary" />,
    description: "Exprime ta créativité : télécharge le kit média et crée ton chef-d'œuvre.",
    details: ["Télécharge le kit média avec tous les extraits exclusifs.", "Crée ta vidéo."]
  }, {
    id: 3,
    title: "Publication",
    icon: <Upload className="h-6 w-6 text-primary" />,
    description: "Fais sensation sur TikTok et ReCut : partage ta création et récolte tes gains.",
    details: ["Publie sur TikTok et gagne 0,5 € pour 1 000 vues.", "Publie aussi sur ReCut pour booster ta visibilité et avoir la chance que Studiocanal te contacte pour acheter ta création."]
  }];

  const DetailsTrigger = memo(({ step }: DetailsTriggerProps) => {
    if (isMobile) {
      return <Dialog>
          <DialogTrigger asChild>
            <button className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
              <Info className="h-4 w-4" />
              <span>Plus de détails</span>
            </button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{step.title}</DialogTitle>
              <DialogDescription>{step.description}</DialogDescription>
            </DialogHeader>
            <ul className="space-y-3 mt-4">
              {step.details.map((detail, i) => <li key={i} className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs text-primary font-bold">{i + 1}</span>
                  </div>
                  <span>{detail}</span>
                </li>)}
            </ul>
          </DialogContent>
        </Dialog>;
    }
    return <Tooltip>
        <TooltipTrigger asChild>
          <button className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
            <Info className="h-4 w-4" />
            <span>Plus de détails</span>
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs bg-card border shadow-lg p-4 rounded-xl">
          <ul className="space-y-2">
            {step.details.map((detail, i) => <li key={i} className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs text-primary font-bold">{i + 1}</span>
                </div>
                <span className="text-sm text-right w-full">{detail}</span>
              </li>)}
          </ul>
        </TooltipContent>
      </Tooltip>;
  });
  
  DetailsTrigger.displayName = "DetailsTrigger";

  return (
    <section id="how-it-works" className="py-20 bg-background/90">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Comment ça marche ?</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">2 minutes suffisent pour remplir le formulaire et démarrer l'aventure.</p>
        </div>
        
        <TooltipProvider delayDuration={300}>
          <motion.div 
            className="max-w-4xl mx-auto" 
            initial="hidden" 
            whileInView="visible" 
            variants={containerVariants}
            viewport={{ once: true, amount: 0.1 }}
          >
            <div className="relative">
              <div className="absolute left-1/2 top-10 bottom-12 w-0.5 bg-gradient-to-b from-primary/30 via-primary/40 to-primary/30 hidden md:block" />
              
              <div className="space-y-16 md:space-y-32 relative">
                {steps.map((step, index) => <motion.div key={step.id} className="relative" variants={itemVariants}>
                    {index < steps.length - 1 && <div className="absolute left-1/2 bottom-[-55px] transform -translate-x-1/2 hidden md:block text-primary/60">
                        <ArrowRight className="h-8 w-8 rotate-90" />
                      </div>}
                    
                    <div className={`flex flex-col ${step.id === 2 && index === 1 ? 'md:flex-row-reverse' : 'md:flex-row'} items-center md:items-start gap-6`}>
                      <div className="relative">
                        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center relative z-10">
                          {step.icon}
                        </div>
                        <div className="absolute top-0 left-0 w-6 h-6 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">
                          {step.id}
                        </div>
                      </div>
                      
                      <div className={`text-center ${step.id === 2 && index === 1 ? 'md:text-right' : 'md:text-left'} max-w-xs`}>
                        <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                        <p className="text-base text-muted-foreground">{step.description}</p>
                        
                        <DetailsTrigger step={step} />
                      </div>
                    </div>
                  </motion.div>)}
              </div>
            </div>
          </motion.div>
        </TooltipProvider>
        
        <div className="mt-16 flex justify-center">
          <Button 
            size="lg" 
            className="bg-primary hover:bg-primary/90 text-white" 
            onClick={handleApplicationFormOpen}
          >
            Participer au challenge
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
      
      {showApplicationForm && (
        <ApplicationFormDialog 
          open={showApplicationForm} 
          onOpenChange={setShowApplicationForm} 
        />
      )}
    </section>
  );
});

HowItWorksSection.displayName = "HowItWorksSection";

export default HowItWorksSection;
